import {
  Badge,
  Box,
  Card,
  Group,
  Modal,
  Pagination,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconFile, IconMusic, IconPhoto, IconSearch, IconVideo } from '@tabler/icons-react';
import { formatBytes } from '@leadsync/shared-utils';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';

export type PickedMedia = {
  id: number;
  kind: string;
  mediaUrl: string | null;
  title: string | null;
  fileName: string | null;
  fileSize: number | null;
  mimeType: string | null;
};

function name(item: PickedMedia): string {
  return item.title || item.fileName || item.kind;
}

function Thumb({ item }: { item: PickedMedia }) {
  const [broken, setBroken] = useState(false);
  if ((item.kind === 'image' || item.kind === 'sticker') && item.mediaUrl && !broken) {
    return (
      <img
        src={item.mediaUrl}
        alt={name(item)}
        onError={() => setBroken(true)}
        style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
      />
    );
  }
  const Icon = item.kind === 'audio' ? IconMusic : item.kind === 'video' ? IconVideo : IconFile;
  return (
    <Box
      style={{
        height: 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
      }}
    >
      <Icon size={28} opacity={0.5} />
    </Box>
  );
}

/**
 * Channel-agnostic "Insert from library" picker. Lists the vendor's stored
 * media (select-one). The caller wires the actual send via the channel's
 * sendMediaFromLibrary mutation.
 */
export function MediaLibraryPicker({
  opened,
  onClose,
  onSelect,
  busy,
}: {
  opened: boolean;
  onClose: () => void;
  onSelect: (item: PickedMedia) => void;
  busy?: boolean;
}) {
  const [page, setPage] = useState(1);
  const [searchRaw, setSearchRaw] = useState('');
  const [search] = useDebouncedValue(searchRaw, 300);

  const list = trpc.media.list.useQuery(
    {
      page,
      pageSize: 24,
      sortField: 'createdAt',
      sortDir: 'desc',
      ...(search ? { search } : {}),
    },
    { enabled: opened },
  );
  const items = (list.data?.items ?? []) as PickedMedia[];
  const totalPages = list.data
    ? Math.max(1, Math.ceil(list.data.totalCount / list.data.pageSize))
    : 1;

  return (
    <Modal opened={opened} onClose={onClose} title="Insert from media library" size="lg" centered>
      <Stack gap="sm">
        <TextInput
          leftSection={<IconSearch size={16} />}
          placeholder="Search media…"
          value={searchRaw}
          onChange={(e) => {
            setSearchRaw(e.currentTarget.value);
            setPage(1);
          }}
        />
        {list.isPending ? (
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} height={130} radius="md" />
            ))}
          </SimpleGrid>
        ) : items.length === 0 ? (
          <Box ta="center" py="xl">
            <IconPhoto size={36} opacity={0.4} />
            <Text c="dimmed" mt="sm" size="sm">
              No media found.
            </Text>
          </Box>
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            {items.map((item) => (
              <Card
                key={item.id}
                padding={0}
                radius="md"
                withBorder
                style={{
                  overflow: 'hidden',
                  cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
                onClick={() => !busy && onSelect(item)}
              >
                <Thumb item={item} />
                <Stack gap={2} p={6}>
                  <Text size="xs" fw={500} truncate title={name(item)}>
                    {name(item)}
                  </Text>
                  <Group justify="space-between" gap={2}>
                    <Badge size="xs" variant="light" color="gray">
                      {item.kind}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {formatBytes(item.fileSize)}
                    </Text>
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
        {list.data && list.data.totalCount > 24 && (
          <Group justify="center">
            <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
